import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { askInSession, startNewSession, setCurrentSession } from "../../store/slices/chatSlice";
import "./ChatInput.css";

const MAX_QUESTIONS_PER_SESSION = 5;

interface ChatInputProps {
  hiddenOnMobile?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ hiddenOnMobile }) => {
  const [input, setInput] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const { loading, currentSessionId, messages } = useSelector(
    (state: RootState) => state.chat
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const currentMessages = currentSessionId ? messages[currentSessionId] || [] : [];
  const questionCount = currentMessages.length;
  const isLimitReached = questionCount >= MAX_QUESTIONS_PER_SESSION;

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    // ~4 lines: 16px font * 1.5 line-height * 4 + padding
    const maxH = 100;
    const clamped = Math.min(textarea.scrollHeight, maxH);
    textarea.style.height = `${clamped}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxH ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (input.trim() && !loading && !isLimitReached) {
      const question = { question: input.trim() };

      if (!currentSessionId) {
        dispatch(startNewSession(question));
      } else {
        dispatch(askInSession({ sessionId: currentSessionId, question }));
      }

      setInput("");
    }
  };

  const handleNewSession = () => {
    dispatch(setCurrentSession(null));
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (isLimitReached && !loading) {
    return (
      <div className={`chat-input-container${hiddenOnMobile ? ' input-hidden-mobile' : ''}`}>
        <div className="session-limit-container">
          <span className="session-limit-text">
            Maximale Anzahl von {MAX_QUESTIONS_PER_SESSION} Fragen erreicht.
          </span>
          <button
            type="button"
            className="new-session-btn"
            onClick={handleNewSession}
          >
            Neue Chat-Session beginnen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-input-container${hiddenOnMobile ? ' input-hidden-mobile' : ''}`}>
      <form className="chat-form" onSubmit={handleSubmit}>
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Geben Sie Ihre Frage ein"
            className="chat-input"
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          className="send-btn"
          disabled={!input.trim() || loading}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 8L1 15L3 8L1 1L15 8Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default ChatInput;
